import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton, Typography } from '@mui/material';

import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SxProps, Theme } from '@mui/material/styles';
import type { KeyboardEvent, ReactNode } from 'react';

interface ReportMetricProps {
    title: string;
    icon?: ReactNode;

    primary: ReactNode | string;
    primaryColor?: string;
    primarySx?: SxProps<Theme>;
    primaryTooltip?: string | JSX.Element;

    secondary?: ReactNode | string;
    secondarySx?: SxProps<Theme>;

    children?: ReactNode;
    loading?: boolean;
    sx?: SxProps<Theme>;
    minHeight?: number | string;
    onClick?: () => void;
}

export const ReportMetric = ({
    title,
    icon,
    primary,
    primaryColor,
    primarySx,
    primaryTooltip,
    secondary,
    secondarySx,
    children,
    loading = false,
    sx,
    minHeight = '200px',
    onClick,
}: ReportMetricProps) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <Box
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            sx={{
                flex: '1 1 0',
                minWidth: 160,
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: 2,
                backgroundColor: '#FFFFFF',
                minHeight,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease-out',
                '&:hover': onClick
                    ? {
                          backgroundColor: 'action.hover',
                      }
                    : undefined,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    marginBottom: 0.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        height: '20px',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#000',
                            fontSize: '14px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        {title}
                    </Typography>
                    {primaryTooltip && (
                        <DynamicTooltip
                            content={primaryTooltip}
                            containerStyle={{ paddingTop: '2px' }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 14,
                                    color: 'rgb(113, 113, 122)',
                                }}
                                data-testclass="icon-tooltip"
                            />
                        </DynamicTooltip>
                    )}
                </Box>
                {icon && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: primaryColor ?? 'text.secondary',
                        }}
                    >
                        {icon}
                    </Box>
                )}
            </Box>

            {loading ? (
                <Skeleton
                    variant="text"
                    sx={{
                        fontSize: '36px',
                        lineHeight: '40px',
                        fontWeight: 700,
                        width: '60%',
                        height: '40px',
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        fontSize: '36px',
                        lineHeight: '40px',
                        fontWeight: 700,
                        color: primaryColor ?? 'text.primary',
                        ...primarySx,
                    }}
                >
                    {primary}
                </Typography>
            )}

            {!loading && secondary && (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'rgb(113, 113, 122)',
                        fontSize: '12px',
                        marginTop: '2px',
                        ...secondarySx,
                    }}
                >
                    {secondary}
                </Typography>
            )}

            {!loading && children && (
                <Box
                    sx={{
                        marginTop: 1,
                        paddingTop: 1,
                        borderTop: '1px solid',
                        borderColor: '#e4e4e7',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                    }}
                >
                    {children}
                </Box>
            )}
        </Box>
    );
};
